import React from 'react'
import {
    Box, Checkbox, FormControlLabel, FormGroup, ListItemText,
    MenuItem, Select, SelectChangeEvent, Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { ChartViewKey } from '../../../../assets/constants'
import { updateChartConfig } from '../../../../reducer/chart.reducer'
import { setMultiStrikeScalesAndLinesSettings } from '../../../../reducer/multiStrikeChart.reducer'
import { selectMultiStrikeScalesAndLinesSettings } from '../../../../selectors/chart'
import { ScalesAndLinesSettings as ScalesAndLinesSettingsType } from '../../../chartV2/chart.typings'
import {
    chartSettingsFormGroupSx,
    chartSettingsSectionTitleSx,
} from '../../../chartSettings/chartSettingsLayout'
import { useWidgetSettingsSelectMenuProps } from '../../../widgetDashboard/widgetSettingsMenuContext'

type Props = {
    viewKey: string
}

const checkboxSx = {
    ml: 0,
    '& .MuiFormControlLabel-label': { fontSize: '14px' },
}

const UNDERLYING_OPTIONS = [
    { value: 'showSymbolLine', label: 'Line' },
    { value: 'showSymbolValue', label: 'Value' },
]

const STRIKE_LINE_OPTIONS = [
    { value: 'showIndicatorLabel', label: 'Name' },
    { value: 'showIndicatorValue', label: 'Value' },
]

const MultiStrikeScalesAndLinesSettings = ({ viewKey }: Props) => {
    const dispatch = useDispatch()
    const isWidget = viewKey !== ChartViewKey.MULTI_STRIKES_CHART
    const selectMenuProps = useWidgetSettingsSelectMenuProps()
    const scalesAndLinesSettings = useSelector(selectMultiStrikeScalesAndLinesSettings(viewKey))

    const updateSettings = (partial: Partial<ScalesAndLinesSettingsType>) => {
        const nextSettings = {
            ...scalesAndLinesSettings,
            ...partial,
        }
        if (isWidget) {
            dispatch(updateChartConfig({
                viewKey,
                config: { scalesAndLinesSettings: nextSettings },
                createIfAbsent: false,
            }))
        } else {
            dispatch(setMultiStrikeScalesAndLinesSettings(nextSettings))
        }
    }

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, field: keyof ScalesAndLinesSettingsType) => {
        updateSettings({ [field]: event.target.checked })
    }

    const showSymbolLine = scalesAndLinesSettings.showSymbolLine ?? true
    const showSymbolValue = scalesAndLinesSettings.showSymbolValue ?? true
    const selectedUnderlyingOptions = [
        ...(showSymbolLine ? ['showSymbolLine'] : []),
        ...(showSymbolValue ? ['showSymbolValue'] : []),
    ]

    const handleUnderlyingSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[]
        updateSettings({
            showSymbolLine: values.includes('showSymbolLine'),
            showSymbolValue: values.includes('showSymbolValue'),
        })
    }

    const showIndicatorValue = scalesAndLinesSettings.showIndicatorValue ?? true
    const showIndicatorLabel = scalesAndLinesSettings.showIndicatorLabel ?? true
    const selectedStrikeLineOptions = [
        ...(showIndicatorLabel ? ['showIndicatorLabel'] : []),
        ...(showIndicatorValue ? ['showIndicatorValue'] : []),
    ]

    const handleStrikeLineSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[]
        updateSettings({
            showIndicatorValue: values.includes('showIndicatorValue'),
            showIndicatorLabel: values.includes('showIndicatorLabel'),
        })
    }

    return (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                PRICE LABELS & LINES
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!(scalesAndLinesSettings.alignLabels ?? true)}
                            onChange={(e) => handleCheckboxChange(e, 'alignLabels')}
                        />
                    }
                    label="No overlapping labels"
                    sx={checkboxSx}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: '50% 50%', alignItems: 'center', minHeight: 36 }}>
                    <Typography fontSize="14px">Underlying</Typography>
                    <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={selectedUnderlyingOptions}
                        onChange={handleUnderlyingSelectChange}
                        renderValue={(selected) => {
                            if (!(selected as string[]).length) {
                                return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>
                            }
                            return (
                                <Typography fontSize="13px">
                                    {(selected as string[]).map(v => UNDERLYING_OPTIONS.find(o => o.value === v)?.label).join(', ')}
                                </Typography>
                            )
                        }}
                        sx={{ width: '100%', fontSize: '13px', height: 32 }}
                        MenuProps={selectMenuProps}
                    >
                        {UNDERLYING_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value} dense>
                                <Checkbox
                                    checked={selectedUnderlyingOptions.includes(option.value)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                />
                                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px' }} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '50% 50%', alignItems: 'center', minHeight: 36 }}>
                    <Typography fontSize="14px">Strike lines</Typography>
                    <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={selectedStrikeLineOptions}
                        onChange={handleStrikeLineSelectChange}
                        renderValue={(selected) => {
                            if (!(selected as string[]).length) {
                                return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>
                            }
                            return (
                                <Typography fontSize="13px">
                                    {(selected as string[]).map(v => STRIKE_LINE_OPTIONS.find(o => o.value === v)?.label).join(', ')}
                                </Typography>
                            )
                        }}
                        sx={{ width: '100%', fontSize: '13px', height: 32 }}
                        MenuProps={selectMenuProps}
                    >
                        {STRIKE_LINE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value} dense>
                                <Checkbox
                                    checked={selectedStrikeLineOptions.includes(option.value)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                />
                                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px' }} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </FormGroup>
        </Box>
    )
}

export default MultiStrikeScalesAndLinesSettings
